const sliderRegistry = new WeakSet();
const deckRegistry = new WeakSet();

function getStep(track) {
  const firstCard = track.querySelector("[data-about-slider-card], .about-dev__team-card");

  if (!firstCard) {
    return track.clientWidth;
  }

  const cardStyles = window.getComputedStyle(firstCard);
  const trackStyles = window.getComputedStyle(track);
  const cardWidth = firstCard.getBoundingClientRect().width;
  const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || cardStyles.marginRight || "0");

  return Math.max(cardWidth + gap, track.clientWidth * 0.8);
}

function updateButtons(track, prevButton, nextButton) {
  if (!prevButton || !nextButton) {
    return;
  }

  const maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);

  prevButton.disabled = track.scrollLeft <= 4;
  nextButton.disabled = track.scrollLeft >= maxScrollLeft - 4;
}

function bindSlider(slider) {
  if (sliderRegistry.has(slider)) {
    return;
  }

  const track = slider.querySelector("[data-about-slider-track], [data-about-team-track]");
  const prevButton = slider.parentElement?.querySelector("[data-about-slider-prev], [data-about-team-prev]");
  const nextButton = slider.parentElement?.querySelector("[data-about-slider-next], [data-about-team-next]");

  if (!track || !prevButton || !nextButton) {
    return;
  }

  prevButton.addEventListener("click", () => {
    track.scrollBy({ left: -getStep(track), behavior: "smooth" });
  });

  nextButton.addEventListener("click", () => {
    track.scrollBy({ left: getStep(track), behavior: "smooth" });
  });

  track.addEventListener("scroll", () => updateButtons(track, prevButton, nextButton), { passive: true });
  window.addEventListener("resize", () => updateButtons(track, prevButton, nextButton));

  updateButtons(track, prevButton, nextButton);
  sliderRegistry.add(slider);
}

function setDeckHeight(deck, activePanel) {
  if (!deck || !activePanel) {
    return;
  }

  deck.style.height = `${activePanel.offsetHeight}px`;
}

function updateDeckUI(root, deck, panels, activeSection) {
  const activeIndex = panels.findIndex((panel) => panel.dataset.aboutSectionPanel === activeSection);
  const track = deck.querySelector("[data-about-section-track]");

  panels.forEach((panel) => {
    const isActive = panel.dataset.aboutSectionPanel === activeSection;
    panel.classList.toggle("is-active", isActive);
    panel.setAttribute("aria-hidden", isActive ? "false" : "true");
  });

  if (track && activeIndex >= 0) {
    track.style.transform = `translate3d(${-100 * activeIndex}%, 0, 0)`;
  }

  root.querySelectorAll("[data-about-section-trigger]").forEach((trigger) => {
    const isActive = trigger.dataset.aboutSectionTrigger === activeSection;
    trigger.classList.toggle("is-active", isActive);
  });

  const activePanel = panels.find((panel) => panel.dataset.aboutSectionPanel === activeSection);

  if (activePanel) {
    setDeckHeight(deck, activePanel);
  }
}

function bindDeck(deck, root = document) {
  if (deckRegistry.has(deck)) {
    return;
  }

  const track = deck.querySelector("[data-about-section-track]");
  const panels = [...deck.querySelectorAll("[data-about-section-panel]")];

  if (!track || !panels.length) {
    return;
  }

  const sections = panels.map((panel) => panel.dataset.aboutSectionPanel);
  let activeSection = panels.find((panel) => panel.classList.contains("is-active"))?.dataset.aboutSectionPanel || sections[0];

  const activateSection = (nextSection, shouldScroll = false) => {
    if (!sections.includes(nextSection)) {
      return;
    }

    activeSection = nextSection;
    updateDeckUI(root, deck, panels, activeSection);

    if (shouldScroll) {
      deck.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  root.querySelectorAll("[data-about-section-trigger]").forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      activateSection(trigger.dataset.aboutSectionTrigger, trigger.hasAttribute("data-about-section-scroll"));
    });
  });

  deck.querySelectorAll("[data-about-section-direction]").forEach((button) => {
    button.addEventListener("click", () => {
      const currentIndex = sections.indexOf(activeSection);
      const delta = Number(button.dataset.aboutSectionDirection || 0);
      const nextIndex = (currentIndex + delta + sections.length) % sections.length;

      activateSection(sections[nextIndex]);
    });
  });

  window.addEventListener("resize", () => {
    const activePanel = panels.find((panel) => panel.dataset.aboutSectionPanel === activeSection);
    setDeckHeight(deck, activePanel);
  });

  updateDeckUI(root, deck, panels, activeSection);
  deckRegistry.add(deck);
}

function bindOurStoryHeroToc(root) {
  const toc = root.querySelector(".our-story-dev__hero-toc");
  const toggle = root.querySelector(".our-story-dev__hero-toc-plus");

  if (!toc || !toggle || toggle.dataset.ourStoryTocBound === "true") {
    return;
  }

  toggle.addEventListener("click", () => {
    const expanded = toc.classList.toggle("is-expanded");
    toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    toggle.setAttribute("aria-label", expanded ? "Close contents" : "Open contents");
  });

  toggle.dataset.ourStoryTocBound = "true";
}

function bindAboutHeroToc(root) {
  const toc = root.querySelector("[data-about-hero-toc]");
  const toggle = root.querySelector("[data-about-hero-toc-toggle]");

  if (!toc || !toggle || toggle.dataset.aboutHeroTocBound === "true") {
    return;
  }

  const setExpanded = (expanded) => {
    toc.classList.toggle("is-collapsed", !expanded);
    toc.setAttribute("aria-hidden", expanded ? "false" : "true");
    toggle.classList.toggle("is-expanded", expanded);
    toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    toggle.setAttribute("aria-label", expanded ? "Close About sections" : "Open About sections");
  };

  toggle.addEventListener("click", () => {
    setExpanded(toggle.getAttribute("aria-expanded") !== "true");
  });

  setExpanded(false);
  toggle.dataset.aboutHeroTocBound = "true";
}

export default function initAboutDev(root = document) {
  root.querySelectorAll("[data-about-team-slider], [data-about-slider]").forEach(bindSlider);
  root.querySelectorAll("[data-about-section-deck]").forEach((deck) => bindDeck(deck, root));
  bindAboutHeroToc(root);
  bindOurStoryHeroToc(root);
}
